import {http} from '@/services/http.js'
import {joinPaths} from '@/helpers/url.js'
import {createFormData, convertKeysToCamelCase, convertKeysToSnakeCase} from '@/helpers/object.js'
import {valueToJSON, toSnakeCase} from '@/helpers/string.js'
import {appVersion} from '@/constants/app.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useTeams} from '@/composables/use-teams.js'
import {useEnv} from '@/composables/use-env.js'

//Ignore keys
const ignoreKeys = [
  '_destroy',
]

/**
 * Base API class – This is a wrapper around the HTTP service
 */
export class BaseApi {

  //Props
  basePath = ''
  convertParamKeys = false
  convertDataKeys = false
  convertJsonKeys = false

  /**
   * Generic request handler
   */
  async request(path, method, params, data, config) {
    const url = await this.makeUrl(path)
    const cfg = await this.makeRequestConfig(method, params, data, config)
    return http
      .request(url, cfg)
      .then(json => this.convertJson(json))
      .catch(error => this.handleError(error))
  }

  /**
   * Handle error
   */
  handleError(error) {

    //Run through processor
    const {processError} = useErrorHandling()
    processError(error)

    //Rethrow so that the error can be handled by the caller
    throw error
  }

  /**************************************************************************
   * Convenience helper methods
   ***/

  /**
   * Helper wrapper for generic GET requests
   */
  async get(path, params, config) {
    return this.request(path, 'get', params, null, config)
  }

  /**
   * Helper wrapper for generic POST requests
   */
  async post(path, data, config) {
    return this.request(path, 'post', null, data, config)
  }

  /**
   * Helper wrapper for generic PATCH requests
   */
  async patch(path, data, config) {
    return this.request(path, 'patch', null, data, config)
  }

  /**
   * Helper wrapper for generic PUT requests
   */
  async put(path, data, config) {
    return this.request(path, 'put', null, data, config)
  }

  /**
   * Helper wrapper for generic DELETE requests
   */
  async delete(path, params, config) {
    return this.request(path, 'delete', params, null, config)
  }

  /**
   * Helper wrapper for generic file upload requests
   */
  async upload(path, data, config = {}) {
    config.timeout = config.timeout ?? false
    config.body = this.createFormData(data)
    return this.request(path, 'post', null, null, config)
  }

  /**************************************************************************
   * Request helpers
   ***/

  /**
   * Helper to make URL for a request based on relative path
   */
  async makeUrl(path) {

    //No base path
    const {basePath} = this
    if (!basePath) {
      return path
    }

    //Full path is absolute
    if (path.startsWith('https')) {
      return path
    }

    //Base path is absolute
    if (basePath.startsWith('https')) {
      return joinPaths(basePath, path)
    }

    //Prepend API base URL
    const {apiBaseUrl} = useEnv()
    return joinPaths(apiBaseUrl.value, basePath, path)
  }

  /**
   * Helper to make request config
   */
  async makeRequestConfig(method, params, data, config = {}) {

    //Set method, params and data
    config.method = method
    config.params = this.convertParams(params)
    config.json = this.convertData(data)

    //Append default config
    await this.appendDefaultConfig(config)

    //Return
    return config
  }

  /**
   * Append default config
   */
  async appendDefaultConfig(config) {

    //Get git hash and Teams app version
    const {gitHash} = useEnv()
    const {isInTeams, teamsAppId, teamsAppVersion} = useTeams()

    //Ensure headers object is present
    config.headers ??= {}

    //Append to headers
    config.headers['X-App-Version'] = appVersion
    config.headers['X-App-Git-Hash'] = gitHash.value

    //Only append Teams app version if in Teams
    if (isInTeams.value) {
      config.headers['X-Teams-App-Id'] = teamsAppId.value
      config.headers['X-Teams-App-Version'] = teamsAppVersion.value
    }

    //Include cookie credentials
    config.credentials = 'include'
  }

  /**************************************************************************
   * Conversion helpers
   ***/

  /**
   * Convert incoming JSON data for the front-end
   */
  convertJson(json) {

    //Empty response is ok
    if (!json) {
      return undefined
    }

    //Not JSON
    if (typeof json !== 'object') {
      throw new Error('Invalid JSON response received')
    }

    //Convert keys
    if (this.convertJsonKeys) {
      json = convertKeysToCamelCase(json, ignoreKeys)
    }

    //Pass on the JSON
    return json
  }

  /**
   * Convert outgoing data for the backend
   */
  convertData(data) {

    //No data
    if (!data) {
      return undefined
    }

    //Convert to JSON
    if (data && typeof data === 'object' && !(data instanceof FormData)) {
      data = valueToJSON(data)
    }

    //Convert keys
    if (this.convertDataKeys) {
      data = convertKeysToSnakeCase(data, ignoreKeys)
    }

    //Pass on the data
    return data
  }

  /**
   * Convert outgoing params for the backend
   */
  convertParams(params) {

    //No params
    if (!params) {
      return undefined
    }

    //Convert to JSON
    if (params && typeof params === 'object') {
      params = valueToJSON(params)
    }

    //Convert keys
    if (this.convertParamKeys) {
      params = convertKeysToSnakeCase(params)
      if (params) {
        if (params.sort) {
          params.sort = this.convertSortParam(params.sort)
        }
        if (params.fields) {
          params.fields = this.convertFieldsParam(params.fields)
        }
      }
    }

    //Pass on the params
    return params
  }

  /**
   * Convert fields param
   */
  convertFieldsParam(fields) {
    return fields.map(field => toSnakeCase(field))
  }

  /**
   * Convert sort param
   */
  convertSortParam(sort) {
    return sort
      .split(',')
      .map(() => {
        const isDesc = sort.match(/^-/)
        const snake = toSnakeCase(sort)
        return isDesc ? `-${snake}` : snake
      })
      .join(',')
  }

  /**
   * Create form data
   */
  createFormData(data) {
    data = this.convertData(data)
    return createFormData(data)
  }
}
